/**
 * Responsibility: turn one natural rock arch's parameters into the ring stack that draws and masks it.
 *
 * ## Why an arch is the same shape of problem as a hoodoo
 *
 * A rock column is a stack of polygon rings threaded up a vertical line. An arch is a stack of polygon rings
 * threaded along a *curve* — up one leg, over the crown, down the other. Nothing else differs, so the ring stack
 * emitter and the depth-mask emitter written for columns and buildings both serve it unchanged. This module only
 * has to produce the rings.
 *
 * ## The curve is a superellipse, and that choice is the whole silhouette
 *
 * A semicircle reads as a bridge, not an arch: real arches have near-vertical legs and a broad flattish crown.
 * `|x/a|^n + |y/b|^n = 1` with `n` above 2 gives exactly that — legs that rise steeply, shoulders that turn, a
 * crown that is wide rather than pointed. One exponent controls the entire character.
 *
 * ## Sampled by angle, not by horizontal position
 *
 * Walking the curve in even steps of `x` is the trap here. With a high exponent almost all of the height change
 * happens in the last few percent of the span, so even steps put nine rings across the crown and one across the
 * whole leg. The standard superellipse angle parametrisation spreads them properly instead.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

import { segmentsForRingStack, maskForRingStack } from './structureGeometry.js';
import { ARCHES } from '../config.js';

/** Superellipse exponent as the power the parametrisation actually uses. */
function anglePower() {
  return 2 / ARCHES.SHOULDER_EXPONENT;
}

/**
 * Signed power, so the parametrisation works either side of the crown.
 *
 * @param {number} value
 * @param {number} power
 * @returns {number}
 */
function signedPow(value, power) {
  return Math.sign(value) * Math.pow(Math.abs(value), power);
}

/**
 * The crown's centreline height, in world units above the arch's ground.
 *
 * The stated `height` is the top of the *rock*, so the centreline has to sit one crown thickness below it.
 * Getting this backwards makes every arch overshoot its own declared height, which then disagrees with the
 * elevation the tile reports and with anything that reasons about how tall a structure is.
 *
 * @param {object} arch
 * @returns {number}
 */
export function crownCentreHeight(arch) {
  return Math.max(1, arch.height - arch.crownThickness);
}

/**
 * Cross-section radius at a point along the curve.
 *
 * Thicker at the feet than at the crown, because that is what erosion leaves: the span is the part that has been
 * thinned. A uniform tube reads as pipework.
 *
 * @param {object} arch
 * @param {number} alongness - 0 at the crown, 1 at a foot.
 * @returns {number}
 */
function radiusAt(arch, alongness) {
  const thickening = 1 + (ARCHES.LEG_THICKENING - 1) * Math.pow(alongness, ARCHES.LEG_TAPER_POWER);
  return arch.crownThickness * thickening;
}

/**
 * The ring stack for an arch, ready for both line and mask emission.
 *
 * @param {object} arch
 * @param {number} arch.x - Centre of the opening, world coordinate.
 * @param {number} arch.z
 * @param {number} arch.groundY - Where the feet sit.
 * @param {number} arch.height - Top of the rock above `groundY`.
 * @param {number} arch.halfSpan - Horizontal distance from centre to a foot's centreline.
 * @param {number} arch.crownThickness - Cross-section radius at the crown.
 * @param {number} arch.heading - Rotation of the plane the arch stands in, radians.
 * @param {number} arch.sides - Polygon sides of the cross-section.
 * @param {number} arch.spin - Cross-section rotation offset in turns.
 * @param {number} arch.stops - Rings along the curve.
 * @returns {{rings: Array<{points: Array<[number, number, number]>}>}}
 */
export function archSolid(arch) {
  const power = anglePower();
  const crownY = crownCentreHeight(arch);

  // The plane the arch stands in, and the one horizontal direction perpendicular to it.
  const alongX = Math.cos(arch.heading);
  const alongZ = Math.sin(arch.heading);
  const outX = -alongZ;
  const outZ = alongX;

  const rings = [];

  for (let i = 0; i < arch.stops; i += 1) {
    /*
      Angle from one foot to the other. `t = 0` is the foot on the `+along` side, `t = PI` the other, and `PI/2`
      is the crown.
    */
    const t = (i / (arch.stops - 1)) * Math.PI;
    const cos = Math.cos(t);
    const sin = Math.sin(t);

    // Centreline position within the plane: horizontal offset from centre, and height above the ground.
    const along = arch.halfSpan * signedPow(cos, power);
    const lift = crownY * Math.pow(sin, power);

    /*
      Tangent to the curve, scaled by a positive factor that cancels the singularity in the analytic derivative.

      The true derivative is infinite at the feet, where the curve is vertical, and infinite in the other
      component at the crown. Multiplying through by `|cos t|^(1-p) |sin t|^(1-p)` — positive, so the *direction*
      is untouched — leaves an expression that is exact and finite everywhere, including both endpoints. That is
      what makes the foot rings come out perfectly horizontal instead of merely nearly so.
    */
    const tangentAlong = -arch.halfSpan * sin * Math.pow(sin, 1 - power);
    const tangentUp = crownY * cos * Math.pow(Math.abs(cos), 1 - power);
    const tangentLength = Math.hypot(tangentAlong, tangentUp) || 1;

    /*
      Normal to the curve, still within the arch's plane. Together with the out-of-plane direction it spans the
      ring, so every ring is square to the curve and the tube never pinches.
    */
    const normalAlong = -tangentUp / tangentLength;
    const normalUp = tangentAlong / tangentLength;

    const radius = radiusAt(arch, Math.abs(cos));

    const centreX = arch.x + alongX * along;
    const centreZ = arch.z + alongZ * along;
    const centreY = arch.groundY + lift;

    const points = [];
    for (let k = 0; k < arch.sides; k += 1) {
      const angle = ((k + (arch.spin ?? 0)) / arch.sides) * Math.PI * 2;
      const inPlane = Math.cos(angle) * radius;
      const outPlane = Math.sin(angle) * radius;

      points.push([
        centreX + alongX * normalAlong * inPlane + outX * outPlane,
        centreY + normalUp * inPlane,
        centreZ + alongZ * normalAlong * inPlane + outZ * outPlane,
      ]);
    }

    rings.push({ points });
  }

  return { rings };
}

/** Segments one arch occupies. */
export function segmentsForArch(arch) {
  return segmentsForRingStack(arch.stops, arch.sides);
}

/** Mask vertices and triangles one arch occupies. */
export function maskForArch(arch) {
  return maskForRingStack(arch.stops, arch.sides);
}

/**
 * The two feet of an arch, as world coordinates.
 *
 * **The single authoritative source for where an arch's ground contact is.** Placement samples terrain here,
 * the clearance test measures distance from the segment between them, and the geometry above puts its end rings
 * at the same places — so none of the three can disagree about how much ground an arch occupies.
 *
 * @param {{x: number, z: number, halfSpan: number, heading: number}} arch
 * @returns {[[number, number], [number, number]]}
 */
export function archFeet(arch) {
  const alongX = Math.cos(arch.heading) * arch.halfSpan;
  const alongZ = Math.sin(arch.heading) * arch.halfSpan;

  return [
    [arch.x + alongX, arch.z + alongZ],
    [arch.x - alongX, arch.z - alongZ],
  ];
}

/**
 * Widest the cross-section ever gets, in world units.
 *
 * The legs, always — see `radiusAt`. This is the radius the ground footprint has to reserve, for the same reason
 * a hoodoo reserves its caprock rather than its footing.
 *
 * @param {object} arch
 * @returns {number}
 */
export function archWidestRadius(arch) {
  return radiusAt(arch, 1);
}
